import {
  useEffect,
  useState
} from "react";
import {
  useParams,
  useNavigate,
  Link
} from "react-router-dom";
import {
  User,
  UserRound,
  ArrowLeft,
  Pencil
} from "lucide-react";

import {
  getStudentById
} from "../api/student/studentApi";
import {
  getStudentByIdForPortal
} from "../api/student/studentPortalApi";

// Safe UI imports with fallbacks
import * as UI from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const Button = UI.Button || (({ children, onClick, variant, size, className, leftIcon }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-xl text-xs font-bold transition-all ${ variant === "ghost" ? "hover:bg-slate-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200" : "bg-slate-900 text-white hover:bg-slate-800" } ${size === "sm" ? "px-3 py-1.5" : "px-4 py-2"} ${className}`}
  >
    {leftIcon}
    {children}
  </button>
));

const Badge = UI.Badge || (({ children, className }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-extrabold ${className}`}>
    {children}
  </span>
));

// Borderless Card Container
const Card = ({ className, children }) => (
  <div className={`bg-white dark:bg-neutral-900 rounded-2xl ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title }) => (
  <h2 className="text-xs font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-4">
    {title}
  </h2>
);

const DocBadge = ({ label, submitted }) => (
  <span
    className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${ submitted ? "bg-emerald-50 text-emerald-700" : "bg-neutral-50 dark:bg-neutral-950 text-neutral-400 dark:text-neutral-500" }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${submitted ? "bg-emerald-500" : "bg-slate-300"}`} />
    {label} ({submitted ? "Submitted" : "Pending"})
  </span>
);

const STATUS_COLORS = {
  ENROLLED: "bg-emerald-50 text-emerald-700",
  LEFT: "bg-rose-50 text-rose-700",
  TRANSFERRED: "bg-amber-50 text-amber-800",
  GRADUATED: "bg-sky-50 text-sky-700",
};

const DataTable = ({ rows }) => (
  <div className="overflow-hidden rounded-xl">
    <table className="w-full text-left text-xs">
      <tbody className="divide-y divide-slate-50">
        {rows.map(([label, value], idx) => (
          <tr key={idx} className={idx % 2 === 0 ? "bg-white dark:bg-neutral-900" : "bg-neutral-50 dark:bg-neutral-900/40"}>
            <td className="w-1/3 py-2.5 px-3 font-bold text-neutral-400 dark:text-neutral-500">
              {label}
            </td>
            <td className="py-2.5 px-3 font-semibold text-neutral-800 dark:text-neutral-100">
              {value || <span className="text-neutral-300 dark:text-neutral-600 font-normal italic">Not provided</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StudentDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const homeUrl = isPortal ? "/staff-home" : "/school-home";
  const studentListUrl = isPortal ? "/staff-panel/students" : "/students";

  useEffect(() => {
    const load = async () => {
      try {
        const data = isPortal
          ? await getStudentByIdForPortal(id)
          : await getStudentById(id);

        setStudent(data);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to load student"
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isPortal]);

  const handleBack = () => {
    navigate(studentListUrl);
  };

  const handleEdit = () => {
    navigate(
      isPortal
        ? `/staff-panel/students/${id}/edit`
        : `/students/${id}/edit`
    );
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : null;

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto text-center font-bold text-xs text-neutral-400 dark:text-neutral-500 py-16">
        Loading student record...
      </div>
    );
  }

  if (!student) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-rose-50 text-rose-700 p-4 rounded-2xl text-xs font-bold">
          {"Student record not found"}
        </div>
      </div>
    );
  }

  const fullName = [
    student.firstName,
    student.middleName,
    student.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 bg-neutral-50 dark:bg-neutral-950 min-h-screen">
      {/* Top Header & Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <nav className="mb-1 flex items-center gap-2 text-xs font-extrabold text-neutral-400 dark:text-neutral-500">
            <Link to={homeUrl} className="hover:text-indigo-600 transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to={studentListUrl} className="hover:text-indigo-600 transition-colors">
              Student Directory
            </Link>
            <span>/</span>
            <span className="text-neutral-800 dark:text-neutral-100">Student Profile</span>
          </nav>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Student Overview
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBack}
            leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          >
            Back to List
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleEdit}
            leftIcon={<Pencil className="h-3.5 w-3.5" />}
          >
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Primary Hero Profile Card */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-neutral-900 flex items-center justify-center text-neutral-600 dark:text-neutral-300 shrink-0">
            {student.gender === "Female" ? (
              <UserRound className="h-8 w-8" />
            ) : (
              <User className="h-8 w-8" />
            )}
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {fullName}
              </h1>
              <Badge className={STATUS_COLORS[student.status] || "bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200"}>
                {student.status}
              </Badge>
              <Badge
                className={
                  student.active
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }
              >
                {student.active ? "Active" : "Inactive"}
              </Badge>
            </div>

            <p className="text-xs font-bold text-neutral-500 dark:text-neutral-400">
              Class {student.classId?.className || "N/A"} &bull; Section {student.sectionId?.sectionName || "N/A"}
            </p>
            <p className="text-[11px] font-mono font-semibold text-neutral-400 dark:text-neutral-500">
              ID: {student.studentId}
            </p>
          </div>
        </div>
      </Card>

      {/* Multi-Card Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Basic Information */}
        <Card className="p-6">
          <CardHeader title="Basic Information" />
          <DataTable
            rows={[
              ["Admission No", student.admissionNo],
              ["Roll Number", student.rollNumber],
              ["Gender", student.gender],
              ["Date of Birth", formatDate(student.dateOfBirth)],
              ["Blood Group", student.bloodGroup],
              ["Category", student.category],
              ["Enrolled On", formatDate(student.enrolledOn)],
              ["Transport Mode", student.transportMode],
              ["Address", student.address],
            ]}
          />
        </Card>

        {/* Right Column Grid Stack */}
        <div className="space-y-6">
          {/* Card 2: Academic Information */}
          <Card className="p-6">
            <CardHeader title="Academic Information" />
            <DataTable
              rows={[
                ["Class", student.classId?.className ? `Class ${student.classId.className}` : null],
                ["Section", student.sectionId?.sectionName ? `Section ${student.sectionId.sectionName}` : null],
              ]}
            />
          </Card>

          {/* Card 3: Parent / Guardian Information */}
          <Card className="p-6">
            <CardHeader title="Parent / Guardian Information" />
            <DataTable
              rows={[
                ["Father's Name", student.fatherName],
                ["Mother's Name", student.motherName],
                ["Guardian Name", student.guardianName],
                ["Primary Contact", student.primaryContact],
                ["Secondary Contact", student.secondaryContact],
                ["Email", student.email],
              ]}
            />
          </Card>
        </div>
      </div>

      {/* Card 4: Documents Submitted */}
      <Card className="p-6">
        <CardHeader title="Documents Submitted" />
        <div className="flex flex-wrap gap-2.5">
          <DocBadge
            label="Birth Certificate"
            submitted={student.documentsSubmitted?.birthCertificate}
          />
          <DocBadge
            label="Transfer Certificate"
            submitted={student.documentsSubmitted?.transferCertificate}
          />
          <DocBadge
            label="Aadhaar Card"
            submitted={student.documentsSubmitted?.aadhaarCard}
          />
          <DocBadge
            label="Photographs"
            submitted={student.documentsSubmitted?.photographs}
          />
          <DocBadge
            label="Report Card"
            submitted={student.documentsSubmitted?.reportCard}
          />
          <DocBadge
            label="Other"
            submitted={student.documentsSubmitted?.other}
          />
        </div>
      </Card>

      {/* Card 5: Remarks (Conditional) */}
      {student.remarks && (
        <Card className="p-6">
          <CardHeader title="Remarks & Observations" />
          <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-900/70 text-xs font-semibold text-neutral-700 dark:text-neutral-200 leading-relaxed">
            {student.remarks}
          </div>
        </Card>
      )}
    </div>
  );
};

export default StudentDetail;